import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    sendSignInLinkToEmail,
    signInWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    isSignInWithEmailLink,
    signInWithEmailLink,
    sendPasswordResetEmail,
    updateProfile,
    updatePassword,
    signInAnonymously,
    signInWithCustomToken
} from 'firebase/auth';
import { setDoc, doc, updateDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { v1 as uuidv1 } from 'uuid';
import { auth, sendRequest, db } from '../../../../utils/firebase';
import { getFirebaseErrorMessage } from '../../../../view/authentication/firebase-error-codes/errorCodes';
import { closeAuthDialog } from '../authDialog';

// TODO: Remember to change the URL once the backend is deployed with the new URL
const localBackendUrl = 'http://192.168.29.61:8000/api/v2/admin';

const initialState = {
    isLoading: false,
    user: null,
    error: null,
    email: null,
    isVerificationLinkSent: false,
    isMagicLinkSent: false,
    isEmailVerified: false,
    isEmailExists: false,
    isLoggedIn: false,
    isPasswordResetLinkSent: false,
    tempEmail: null,
    reEnteredEmail: null
};

/**
 * Initializes the auth session by listening to Firebase auth state changes.
 *
 * @function initializeSession
 * @returns {Function} Redux thunk that dispatches auth actions based on user state
 * @description
 * - Sets loading state to true initially
 * - Listens for auth state changes using Firebase's onAuthStateChanged
 * - Unsubscribes after first trigger to prevent multiple listeners
 * - If user exists, extracts basic user data and sets authenticated state
 * - If no user, sets appropriate error message
 * - Sets loading to false when complete
 */
export function initializeSession() {
    return (dispatch) => {
        dispatch(setLoading(true));

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe(); // Unregister the listener after first trigger

            if (user) {
                const userData = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    emailVerified: user.emailVerified
                };
                dispatch(setUserData(userData));
                dispatch(setLoggedIn(true));
            } else {
                dispatch(setError('No user is signed in'));
            }

            dispatch(setLoading(false));
        });
    };
}

export const getCustomTokenAndSignIn = createAsyncThunk(
    'getCustomTokenAndSignIn',
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.post(
                `http://192.168.29.61:8000/api/v2/custom-token`,
                payload
            );
            const customToken = response.data.token;

            // Sign in with custom token
            const userCredential = await signInWithCustomToken(auth, customToken);
            const user = userCredential.user;

            // Set user data in Redux state
            const userData = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                emailVerified: user.emailVerified
            };

            dispatch(setUserData(userData));
            dispatch(setLoggedIn(true));
            // dispatch(closeAuthDialog());

            return userData;
        } catch (error) {
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Sign in with email link (magic link) authentication thunk.
 *
 * @function signInWithEmailLinkThunk
 * @async
 * @returns {Object|null} User data if successful, else null if not a sign-in link
 *
 * Process:
 * 1. Verify if current URL is a valid sign-in link
 * 2. Close any open auth dialogs
 * 3. Complete sign-in with Firebase auth
 * 4. Update loginSessions document with status and uid
 * 5. Clean up by removing email and sessionId from localStorage
 * 6. Return formatted user data
 *
 * @throws Will reject with formatted error message on failure
 */
export const signInWithEmailLinkThunk = createAsyncThunk(
    'signInWithEmailLink',
    async ({ email, sessionId }, { rejectWithValue, dispatch }) => {
        try {
            const receiverDeviceId = localStorage.getItem('senderDeviceId') || uuidv1();

            // Step 1
            if (isSignInWithEmailLink(auth, window.location.href)) {
                // Step 2
                dispatch(closeAuthDialog());

                // Step 3
                const result = await signInWithEmailLink(auth, email, window.location.href);

                // Step 4
                await updateDoc(doc(db, 'loginSessions', sessionId), {
                    status: 'verified',
                    verifiedAt: new Date().toLocaleString(),
                    email: email,
                    authUid: result.user.uid,
                    receiverDeviceId: receiverDeviceId
                });

                // Step 5
                localStorage.removeItem('email');
                localStorage.removeItem('sessionId');
                localStorage.removeItem('senderDeviceId');

                const userData = {
                    uid: result.user.uid,
                    email: result.user.email,
                    displayName: result.user.displayName,
                    emailVerified: result.user.emailVerified
                };

                // Step 6
                return userData;
            }

            return null; // Else if link is not a sign-in link
        } catch (error) {
            // reject with formatted error message on failure and remove email and sessionId from localStorage
            localStorage.removeItem('email');
            localStorage.removeItem('sessionId');
            localStorage.removeItem('senderDeviceId');
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * @function registerUser
 * @description Redux async thunk for user registration with Firebase Authentication and backend API
 * @async
 * @param {Object} payload - The registration data
 * @param {Function} thunkAPI.rejectWithValue - Redux Toolkit's utility for handling rejections
 * @returns {Promise<Object>} User data object on success
 * @returns {Object} User data including uid, email, displayName, emailVerified, and status
 * @throws {string} Firebase error message on failure
 *
 * @process
 * 2. Extract user registration details from payload
 * 3. Retrieve or generate device token from localStorage or via sendRequest()
 * 4. Create Firebase user with email link authentication
 * 5. Update Firebase user profile with password if provided
 * 6. Update Firebase user profile with display name if provided
 * 7. Register user with backend API, sending Firebase ID token and device token
 * 8. close the open complete profile dialog
 * 9. clean up the localStorage
 * 10. Return formatted user data object with combined Firebase and backend response information
 *
 * @errorHandling
 * - Catches any errors during the registration process
 * - Formats Firebase errors into readable messages using getFirebaseErrorMessage utility
 * - Returns the formatted error message using rejectWithValue
 */
export const registerUser = createAsyncThunk(
    'registerUser',
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            // Step 1
            const magicLink = localStorage.getItem('magicLink');

            // Step 2
            const { email, password, name, city, dob, phone } = payload;

            // Step 3
            let deviceToken = localStorage.getItem('deviceToken') || (await sendRequest());

            // Step 4
            const userCredential = await signInWithEmailLink(auth, email, magicLink);
            const user = userCredential.user;

            // Step 5
            await updatePassword(user, password);

            // Step 6
            if (name) {
                await updateProfile(user, { displayName: name });
            }

            // Step 7
            const backendResponse = await axios.post(
                `http://192.168.29.61:8000/api/v2/user/register`,
                {
                    email,
                    password,
                    name,
                    dob,
                    phone,
                    city_id: city,
                    id_token: await user.getIdToken(),
                    device_token: deviceToken || undefined
                }
            );

            // Step 8
            dispatch(closeAuthDialog());

            // Step 9
            window.localStorage.removeItem('registerEmail');
            window.localStorage.removeItem('magicLink');

            // Step 10
            const userData = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                emailVerified: user.emailVerified,
                status: backendResponse.data.status
            };

            return userData;
        } catch (error) {
            // Return the error if occurs and clean up localStorage
            window.localStorage.removeItem('registerEmail');
            window.localStorage.removeItem('magicLink');
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Redux async thunk to handle user login with email and password.
 *
 * @function loginWithEmailAndPassword
 * @async
 * @param {Object} credentials - The login credentials
 * @param {string} credentials.email - User's email address
 * @param {string} credentials.password - User's password
 * @returns {Object} User data including Firebase auth info and backend status
 *
 * Process:
 * 1. Authenticate with Firebase using email/password
 * 2. Get ID token from authenticated user
 * 3. Send credentials + ID token to backend API
 * 4. Return combined user data from Firebase and backend
 *
 * @throws Will return rejected promise with error message on failure
 */
export const loginWithEmailAndPassword = createAsyncThunk(
    'loginWithEmailAndPassword',
    async ({ email, password }, { rejectWithValue }) => {
        try {
            // Step 1
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Step 2
            const idToken = await user.getIdToken();

            // Step 3
            const response = await axios.post(`${localBackendUrl}/login`, {
                email,
                password,
                id_token: idToken
            });

            const userData = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                emailVerified: user.emailVerified,
                status: response.data.status
            };

            // Step 4
            return userData;
        } catch (error) {
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

export const guestLogin = createAsyncThunk(
    'guestLogin',
    async ({ email, phone }, { rejectWithValue }) => {
        try {
            // Sign in anonymously
            const userCredential = await signInAnonymously(auth);
            const user = userCredential.user;

            await axios.post(`http://192.168.29.61:8000/api/v2/guest/register`, {
                email: email,
                phone: phone,
                id_token: await user.getIdToken(),
                device_token: localStorage.getItem('deviceToken') || (await sendRequest())
            });

            return {
                uid: user.uid,
                email: null,
                displayName: 'Guest User',
                emailVerified: false
            };
        } catch (error) {
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Redux async thunk for sending a magic link for passwordless sign-in
 *
 * @function sendMagicLink
 * @async
 * @param {string} email - User's email address to send the magic link to
 * @returns {undefined} returns undefined on successful response
 * @throws {string} Firebase error message if the operation fails
 *
 * Process:
 * 1. Generate a unique session ID
 * 2. Config the action url and pass generated session id into url
 * 3. Send the sign in link to email
 * 4. Create a document in loginSessions collection to keep the document until its get verified
 * 5. Set the email and sessionId into localStorage
 * 6. Return the email and session id
 */
export const sendMagicLink = createAsyncThunk(
    'sendMagicLink',
    async (email, { rejectWithValue }) => {
        try {
            // Step 1
            const sessionId = uuidv4();
            const senderDeviceId = uuidv1();

            // Step 2
            const actionCodeSettings = {
                url: `http://localhost:3000/finishSignIn?sessionId=${sessionId}`,
                handleCodeInApp: true
            };

            // Step 3
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);

            // Step 4
            await setDoc(doc(db, 'loginSessions', sessionId), {
                email: email,
                sessionId: sessionId,
                status: 'pending',
                senderDeviceId: senderDeviceId,
                createdAt: new Date().toLocaleString()
            });

            // Step 5
            localStorage.setItem('email', email);
            localStorage.setItem('sessionId', sessionId);
            localStorage.setItem('senderDeviceId', senderDeviceId);

            // Step 6
            return { email, sessionId };
        } catch (error) {
            console.log('Error sending magic link:', error);
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Redux async thunk for sending a magic link for passwordless sign-in
 *
 * @function registerUserMagicLink
 * @async
 * @param {string} email - User's email address to send the magic link to
 * @returns {undefined} returns undefined on successful response
 * @throws {string} Firebase error message if the operation fails
 *
 * @example
 * dispatch(sendMagicLink('user@example.com'))
 *   .then(() => console.log('Magic link sent'))
 *   .catch((error) => console.error(error));
 */
export const registerUserMagicLink = createAsyncThunk(
    'registerUserMagicLink',
    async (email, { rejectWithValue }) => {
        try {
            const actionCodeSettings = {
                url: 'http://localhost:3000/pages/register/flow=newSignup',
                handleCodeInApp: true
            };

            await sendSignInLinkToEmail(auth, email, actionCodeSettings);

            return { email };
        } catch (error) {
            console.log('Error sending magic link:', error);
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Redux async thunk for sending a password reset link to the provided email.
 *
 * @param {string} email - The email address to send the password reset link to
 * @returns {Object} Object containing the email if successful
 * @throws {string} Firebase error message if the operation fails
 *
 * @example
 * dispatch(sendPasswordResetLink('user@example.com'))
 *   .then(() => console.log('Reset email sent'))
 *   .catch((error) => console.error(error));
 */
export const sendPasswordResetLink = createAsyncThunk(
    'sendPasswordResetLink',
    async (email, { rejectWithValue }) => {
        try {
            const actionCodeSettings = {
                url: 'http://localhost:3000/pages/reset-password',
                handleCodeInApp: true
            };

            await sendPasswordResetEmail(auth, email, actionCodeSettings);
            return { email };
        } catch (error) {
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Async thunk action to sign out the current user from Firebase Auth.
 * @function signOutUser
 * @async
 * @returns {Promise<boolean>} Returns true if sign out was successful
 * @throws {Error} Rejects with error message if sign out fails
 * @description
 * It also dispatches an action to update the Redux state to reflect that the user is no longer logged in.
 */
export const signOutUser = createAsyncThunk(
    'signOutUser',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            await signOut(auth);
            dispatch(setLoggedIn(false));
            return true;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Redux async thunk to check if an email exists in the system.
 * Makes a POST request to the backend with the provided email.
 *
 * @function checkEmailExists
 * @param {string} email - The email address to check
 * @returns {Promise<Object>} Response data if successful
 * @throws Will reject with error message if request fails
 * TODO: Remember to change the url once the backend is deployed with new URL
 */
export const checkEmailExists = createAsyncThunk(
    'checkEmailExists',
    async (email, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${localBackendUrl}/check-email`, {
                email
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const firebaseAuthSlice = createSlice({
    name: 'firebaseAuth',
    initialState,
    reducers: {
        resetAuthState: (state) => {
            state.isLoading = false;
            state.error = null;
            state.email = null;
            state.isVerificationLinkSent = false;
            state.isEmailVerified = false;
        },
        setEmailVerified: (state, action) => {
            state.isEmailVerified = action.payload;
        },
        setVerificationLinkSent: (state, action) => {
            state.isVerificationLinkSent = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
            // console.log("Firebase Auth Error:", action.payload);
        },
        setUserData: (state, action) => {
            state.user = action.payload;
            // console.log("User data set:", action.payload);
        },
        setLoggedIn: (state, action) => {
            state.isLoggedIn = action.payload;
            // console.log("User logged in status:", action.payload);
        },
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setPasswordResetLinkSent: (state, action) => {
            state.isPasswordResetLinkSent = action.payload;
            // console.log("Password reset link sent status:", action.payload);
        },
        setTempEmail: (state, action) => {
            state.tempEmail = action.payload;
            // console.log("Temporary email set:", action.payload);
        },
        setNewUserVerfied: (state, action) => {
            state.isNewUser = action.payload;
            console.log('New user verified status:', action.payload);
        },
        setReEnteredEmail: (state, action) => {
            state.reEnteredEmail = action.payload;
            // console.log("Email prompt status:", action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            // checking email existence
            .addCase(checkEmailExists.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(checkEmailExists.fulfilled, (state, action) => {
                state.isEmailExists = action.payload;
                state.isLoading = false;
                // console.log("Email exists:", action.payload);
            })
            .addCase(checkEmailExists.rejected, (state, action) => {
                state.error = action.payload;
                state.isLoading = false;
                // console.log("Error checking email existence:", action.payload);
            });

        // sending magic link
        builder
            .addCase(sendMagicLink.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.isMagicLinkSent = false;
            })
            .addCase(sendMagicLink.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isMagicLinkSent = true;
                state.email = action.payload.email;
                // console.log("Magic link sent to:", action.payload.email);
            })
            .addCase(sendMagicLink.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error sending magic link:", action.payload);
            });

        // register magic link
        builder
            .addCase(registerUserMagicLink.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.isMagicLinkSent = false;
            })
            .addCase(registerUserMagicLink.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isMagicLinkSent = true;
                state.email = action.payload.email;
                // console.log("Magic link sent to:", action.payload.email);
            })
            .addCase(registerUserMagicLink.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error sending magic link:", action.payload);
            });

        // logging in with email and password
        builder
            .addCase(loginWithEmailAndPassword.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginWithEmailAndPassword.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.error = null;
                state.isLoggedIn = true;
                // console.log("User logged in:", action.payload);
            })
            .addCase(loginWithEmailAndPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error logging in:", action.payload);
            });

        // signing out user
        builder
            .addCase(signOutUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signOutUser.fulfilled, (state) => {
                state.isLoading = false;
                state.user = null;
                state.isLoggedIn = false;
                // console.log("User signed out successfully");
            })
            .addCase(signOutUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error signing out:", action.payload);
            });

        // signing in with email link
        builder
            .addCase(signInWithEmailLinkThunk.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signInWithEmailLinkThunk.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload) {
                    state.user = action.payload;
                    state.isLoggedIn = true;
                    state.error = null;
                    state.reEnteredEmail = null;
                    state.tempEmail = null;
                    // console.log("User signed in with email link:", action.payload);
                } else {
                    state.error = 'Not a valid sign-in link';
                }
            })
            .addCase(signInWithEmailLinkThunk.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error signing in with email link:", action.payload);
            });

        // sending password reset link
        builder
            .addCase(sendPasswordResetLink.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(sendPasswordResetLink.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isVerificationLinkSent = true;
                state.email = action.payload.email;
                // console.log("Password reset link sent to:", action.payload.email);
            })
            .addCase(sendPasswordResetLink.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error sending password reset link:", action.payload);
            });

        builder
            .addCase(registerUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.error = null;
                state.isLoggedIn = true;
                // console.log("User created successfully:", action.payload);
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // console.log("Error creating user:", action.payload);
            });
    }
});

export const {
    resetAuthState,
    setEmailVerified,
    setVerificationLinkSent,
    setError,
    setUserData,
    setLoggedIn,
    setLoading,
    setPasswordResetLinkSent,
    setTempEmail,
    setReEnteredEmail
} = firebaseAuthSlice.actions;

export default firebaseAuthSlice.reducer;
